import React, { useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Download, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { useAppStore } from '../../store';
import { objectsToCsv, csvToObjects, downloadCsv } from '../../utils/csvUtils';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { supabase } from '../../lib/supabase';

// Styles for drag and drop area
const dragAreaStyles = {
  default: "border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center transition-colors",
  active: "border-2 border-dashed border-indigo-500 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6 text-center transition-colors"
};

interface ExpenseCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExpenseCsvModal: React.FC<ExpenseCsvModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { expenseCategories, addExpense, expenses } = useAppStore();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importedExpenses, setImportedExpenses] = useState<any[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Generate a template CSV for download
  const generateTemplateCsv = () => {
    // Define the headers and a sample row - removed supplier field
    const headers = [      
      'category',
      'description',
      'amount',
      'vat',
      'payment_method',
      'date',
      'is_recurring',
      'recurrence'
    ];
    
    const sampleData = [
      {
        category: expenseCategories.length > 0 ? expenseCategories[0].name : 'Nombre de categoría',
        description: 'Descripción del gasto',
        amount: 1000,
        vat: 21,
        payment_method: 'cash', // cash, transfer, card, check, mercadopago
        date: new Date().toISOString().split('T')[0], // YYYY-MM-DD
        is_recurring: false,
        recurrence: '' // daily, weekly, monthly, quarterly, yearly (required if is_recurring is true)
      }
    ];
    
    // Convert to CSV
    const csvContent = objectsToCsv(sampleData, headers);
    
    // Download the CSV
    downloadCsv(csvContent, 'plantilla_gastos.csv');
  };

  // Export current expenses to CSV
  const exportExpensesToCsv = () => {
    if (expenses.length === 0) {
      setUploadError('No hay gastos para exportar');
      return;
    }

    // Map expenses to CSV format - removed supplier field
    const expensesForCsv = expenses.map(expense => {
      // Find category name
      const category = expenseCategories.find(cat => cat.id === expense.category_id);
      
      return {
        category: category ? category.name : '',
        description: expense.description,
        amount: expense.amount,
        vat: expense.vat,
        payment_method: expense.payment_method,
        date: expense.date,
        is_recurring: expense.is_recurring,
        recurrence: expense.recurrence || ''
      };
    });

    // Convert to CSV
    const csvContent = objectsToCsv(expensesForCsv);
    
    // Download the CSV
    downloadCsv(csvContent, `gastos_${formatArgentinaDate(new Date().toISOString(), 'yyyyMMdd')}.csv`);
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setValidationErrors([]);

    try {
      // Read the file
      const text = await file.text();
      
      // Parse CSV to objects
      const parsedExpenses = csvToObjects(text);
      
      if (parsedExpenses.length === 0) {
        throw new Error('El archivo CSV está vacío o tiene un formato incorrecto');
      }

      // Validate each expense
      const errors: string[] = [];
      const validExpenses = [];
      
      // Create a map of category names to IDs for quick lookup (case insensitive) 
      const categoryMap = new Map(
        expenseCategories.map(cat => [cat.name.toLowerCase().trim(), cat.id])
      );

      for (let i = 0; i < parsedExpenses.length; i++) {
        const expense = parsedExpenses[i];
        const rowNum = i + 2; // +2 because row 1 is headers, and we're 0-indexed
        
        // Determine category ID from the category field
        let categoryId = null;
        
        if (expense.category && expense.category.trim()) {
          // Look up category ID by name
          const categoryName = expense.category.toLowerCase().trim();
          categoryId = categoryMap.get(categoryName);
          if (!categoryId) {
            errors.push(`Fila ${rowNum}: La categoría "${expense.category}" no existe`);
            continue;
          }
        } else {
          errors.push(`Fila ${rowNum}: La categoría es obligatoria`);
          continue;
        }
        
        if (!expense.description) {
          errors.push(`Fila ${rowNum}: Falta la descripción`);
          continue;
        }
        
        if (!expense.amount || isNaN(Number(expense.amount)) || Number(expense.amount) <= 0) {
          errors.push(`Fila ${rowNum}: El monto debe ser un número mayor a 0`);
          continue;
        }
        
        if (!expense.payment_method || !['cash', 'transfer', 'card', 'check', 'mercadopago'].includes(expense.payment_method)) {
          errors.push(`Fila ${rowNum}: Método de pago inválido. Debe ser: cash, transfer, card, check, mercadopago`);
          continue;
        }
        
        if (!expense.date) {
          errors.push(`Fila ${rowNum}: Falta la fecha`);
          continue;
        }
        
        // Check recurrence if is_recurring is true
        if (expense.is_recurring && 
            (!expense.recurrence || !['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].includes(String(expense.recurrence)))) {
          errors.push(`Fila ${rowNum}: Si el gasto es recurrente, debe especificar una recurrencia válida`);
          continue;
        }

        // Convierte el texto a booleano real
        const isRecurring = String(expense.is_recurring).toLowerCase().trim() === 'true';

        // Monto: acepta "1000", "1000.50" o "1000,50"
        const parsedAmount = parseFloat(
          String(expense.amount).replace(',', '.')
        );

        // IVA: igual
        const parsedVat = parseFloat(
          String(expense.vat || '0').replace(',', '.')
        );
        
        validExpenses.push({
          category_id: categoryId,
          supplier_id: undefined, // No supplier field
          description: expense.description,
          amount: parsedAmount,
          vat: parsedVat,
          payment_method: expense.payment_method,
          date: expense.date,
          is_recurring: isRecurring,
          recurrence: isRecurring ? expense.recurrence : null
        });
      }

      // Set validation errors if any
      if (errors.length > 0) {
        setValidationErrors(errors);
        throw new Error(`Se encontraron ${errors.length} errores en el archivo CSV`);
      }

      // Save valid expenses
      if (validExpenses.length > 0) {
        for (const expense of validExpenses) {
          await addExpense(expense);
        }
        
        // Success with detailed list
        const successMessage = `Se importaron ${validExpenses.length} gastos correctamente:`;
        setUploadSuccess(successMessage);
        // Store the valid expenses for display
        setImportedExpenses(validExpenses);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error('No se pudo importar ningún gasto. Verifique los errores.');
      }
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      setUploadError(error.message || 'Error al procesar el archivo CSV');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === "text/csv" || file.name.endsWith('.csv')) {
        // Process the CSV file
        await processFile(file);
      } else {
        setUploadError("Solo se permiten archivos CSV");
      }
    }
  };

  // Process the uploaded file
  const processFile = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setValidationErrors([]);

    try {
      // Read the file
      const text = await file.text();
      
      // Parse CSV to objects
      const parsedExpenses = csvToObjects(text);
      
      if (parsedExpenses.length === 0) {
        throw new Error('El archivo CSV está vacío o tiene un formato incorrecto');
      }

      // Validate each expense
      const errors: string[] = [];
      const validExpenses = [];
      
      // Create a map of category names to IDs for quick lookup (case insensitive) 
      const categoryMap = new Map(
        expenseCategories.map(cat => [cat.name.toLowerCase().trim(), cat.id])
      );

      for (let i = 0; i < parsedExpenses.length; i++) {
        const expense = parsedExpenses[i];
        const rowNum = i + 2; // +2 because row 1 is headers, and we're 0-indexed
        
        // Determine category ID from the category field
        let categoryId = null;
        
        if (expense.category && expense.category.trim()) {
          // Look up category ID by name
          const categoryName = expense.category.toLowerCase().trim();
          categoryId = categoryMap.get(categoryName);
          if (!categoryId) {
            errors.push(`Fila ${rowNum}: La categoría "${expense.category}" no existe`);
            continue;
          }
        } else {
          errors.push(`Fila ${rowNum}: La categoría es obligatoria`);
          continue;
        }
        
        if (!expense.description) {
          errors.push(`Fila ${rowNum}: Falta la descripción`);
          continue;
        }
        
        if (!expense.amount || isNaN(Number(expense.amount)) || Number(expense.amount) <= 0) {
          errors.push(`Fila ${rowNum}: El monto debe ser un número mayor a 0`);
          continue;
        }
        
        if (!expense.payment_method || !['cash', 'transfer', 'card', 'check', 'mercadopago'].includes(expense.payment_method)) {
          errors.push(`Fila ${rowNum}: Método de pago inválido. Debe ser: cash, transfer, card, check, mercadopago`);
          continue;
        }
        
        if (!expense.date) {
          errors.push(`Fila ${rowNum}: Falta la fecha`);
          continue;
        }
        
        // Check recurrence if is_recurring is true
        if (expense.is_recurring && 
            (!expense.recurrence || !['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].includes(String(expense.recurrence)))) {
          errors.push(`Fila ${rowNum}: Si el gasto es recurrente, debe especificar una recurrencia válida`);
          continue;
        }

        // Convierte el texto a booleano real
        const isRecurring = String(expense.is_recurring).toLowerCase().trim() === 'true';

        // Monto: acepta "1000", "1000.50" o "1000,50"
        const parsedAmount = parseFloat(
          String(expense.amount).replace(',', '.')
        );

        // IVA: igual
        const parsedVat = parseFloat(
          String(expense.vat || '0').replace(',', '.')
        );
                
        validExpenses.push({
          category_id: categoryId,
          supplier_id: undefined, // No supplier field
          description: expense.description,
          amount: parsedAmount,
          vat: parsedVat,
          payment_method: expense.payment_method,
          date: expense.date,
          is_recurring: isRecurring,
          recurrence: isRecurring ? expense.recurrence : null
        });
      }

      // Set validation errors if any
      if (errors.length > 0) {
        setValidationErrors(errors);
        throw new Error(`Se encontraron ${errors.length} errores en el archivo CSV`);
      }

      // Save valid expenses
      if (validExpenses.length > 0) {
        for (const expense of validExpenses) {
          await addExpense(expense);
        }
        
        // Success with detailed list
        const successMessage = `Se importaron ${validExpenses.length} gastos correctamente:`;
        setUploadSuccess(successMessage);
        // Store the valid expenses for display
        setImportedExpenses(validExpenses);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error('No se pudo importar ningún gasto. Verifique los errores.');
      }
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      setUploadError(error.message || 'Error al procesar el archivo CSV');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            Importar Gastos CSV
          </Dialog.Title>
          
          <div className="space-y-6">
            {/* Template Download */}
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 mb-2">
                Plantilla CSV
              </h3>
              <p className="text-sm text-indigo-600 dark:text-indigo-300 mb-4">
                Descarga una plantilla CSV con el formato correcto para importar gastos. Solo se requieren categoría y descripción como campos obligatorios.
              </p>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={generateTemplateCsv}
              >
                Descargar Plantilla
              </Button>
            </div>
            

            {/* CSV Upload */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/30 p-4 rounded-lg"
              onDragEnter={handleDrag}
            >
              <h3 className="text-lg font-medium text-amber-800 dark:text-amber-200 mb-2">
                Importar Gastos desde CSV
              </h3>              
              <p className="text-sm text-amber-600 dark:text-amber-300 mb-2">
                Selecciona un archivo CSV con gastos para importar. Puedes usar la plantilla descargada.
              </p>

              <div
                className={dragActive ? dragAreaStyles.active : dragAreaStyles.default}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center py-4">
                  <Upload className="h-10 w-10 text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {dragActive 
                      ? "Suelta el archivo aquí" 
                      : "Arrastra y suelta un archivo CSV aquí"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">o</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-file"
                    ref={fileInputRef}
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('csv-file')?.click()}
                    disabled={isUploading}
                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  >
                    {isUploading ? 'Procesando...' : 'Seleccionar Archivo CSV'}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Upload Status */}
            {(uploadError || uploadSuccess || validationErrors.length > 0) && (
              <div className={`p-4 rounded-lg ${
                uploadError || validationErrors.length > 0
                  ? 'bg-red-50 dark:bg-red-900/30'
                  : 'bg-green-50 dark:bg-green-900/30'
              }`}>
                {uploadError && (
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{uploadError}</p>
                    </div>
                  </div>
                )}
                
                {validationErrors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-red-700 dark:text-red-300 mb-2">Errores de validación:</p>
                    <ul className="list-disc pl-5 text-sm text-red-600 dark:text-red-400 space-y-1 max-h-40 overflow-y-auto">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {uploadSuccess && (
                  <div className="flex items-start gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div className="w-full overflow-hidden">
                      <p className="font-medium">{uploadSuccess}</p>
                      {importedExpenses.length > 0 && (
                        <div className="mt-2 max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="text-left text-green-800 dark:text-green-200">
                              <tr>
                                <th className="pb-1">Descripción</th>
                                <th className="pb-1">Monto</th>
                                <th className="pb-1">Fecha</th>
                              </tr>
                            </thead>
                            <tbody className="text-green-700 dark:text-green-300">
                              {importedExpenses.map((expense, index) => (
                                <tr key={index}>
                                  <td className="pr-2 py-1">{expense.description}</td>
                                  <td className="pr-2 py-1">${expense.amount.toLocaleString('es-AR')}</td>
                                  <td className="py-1">{formatArgentinaDate(expense.date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-end">
            <Button
              variant="outline"
              onClick={exportExpensesToCsv}
              className="mr-2"
              icon={<Download className="h-4 w-4" />}
            >
              Exportar Gastos
            </Button>
            <Button
              variant="primary"
              onClick={onClose}
            >
              Cerrar
            </Button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExpenseCsvModal;